import React from 'react'
import { MultipleChoiceQuestion } from '../types/nps.types'

interface Props {
    question: MultipleChoiceQuestion
    value: string | undefined
    onChange: (value: string) => void
    error?: string
}

const NpsMultipleChoice: React.FC<Props> = ({ question, value, onChange, error }) => {
    const { id, question: label, options, required } = question
    const groupId = `nps-mc-${id}`
    const errorId = `${groupId}-error`

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-start gap-1">
                <span id={groupId} className="text-[0.75rem] font-semibold text-gray-800">
                    {label}
                </span>
                {required && (
                    <span aria-hidden className="text-red-500">
                        *
                    </span>
                )}
            </div>

            <div
                role="radiogroup"
                aria-labelledby={groupId}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="flex flex-col gap-1.5"
                data-testid={`nps-multiple-choice-${id}`}
            >
                {options.map((option) => {
                    const selected = value === option.value
                    const optionId = `${groupId}-${option.value}`
                    return (
                        <label
                            key={option.value}
                            htmlFor={optionId}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-[0.75rem] transition-colors ${selected ? 'border-primary bg-primary/10' : 'border-gray-300 bg-white hover:border-primary'}`}
                        >
                            <input
                                id={optionId}
                                type="radio"
                                name={groupId}
                                value={option.value}
                                checked={selected}
                                onChange={() => onChange(option.value)}
                                className="accent-primary"
                                data-testid={`nps-multiple-choice-${id}-option-${option.value}`}
                            />
                            <span className="text-gray-800">{option.label}</span>
                        </label>
                    )
                })}
            </div>

            {error && (
                <p id={errorId} role="alert" className="text-error text-[0.583rem]">
                    {error}
                </p>
            )}
        </div>
    )
}

export default NpsMultipleChoice
